import React, { useState, useEffect } from "react";
import { useForm } from "@mantine/form";
import {
  TextInput,
  Button,
  Group,
  Title,
  Select,
  NumberInput,
  Box,
  Image as MantineImage,
  createStyles,
  Space,
  UnstyledButton,
  Text,
  Portal,
  Overlay,
  Loader,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconCurrencyDollar,
  IconPercentage,
  IconTrash,
} from "@tabler/icons-react";
import {
  saveBenefitLevel,
  getLevels,
  deleteBenefitLevel,
  updateBenefitLevel,
} from "../../services/api.service";
import BenefitLevelImageDropzone from "./BenefitLevelImageDropzone";
import { modals } from "@mantine/modals";
import { useDispatch, useSelector } from "react-redux";

// Custom styles
const useStyles = createStyles((theme) => ({
  imageContainer: {
    border: "1px solid #CCCCCC",
    padding: 3,
    borderRadius: theme.radius.sm,
  },
}));

const BenefitLevelForm = ({ benefit, reload }) => {
  // Hooks
  const [isLoading, toggleLoading] = useState(false);
  const [fileURL, setFileURL] = useState("");
  const [previewFileURL, setPreviewFileURL] = useState("");
  const [levels, setLevels] = useState([]);
  const [isLogoChanged, toggleIsLogoChanged] = useState(false);
  const [isPreviewLogoChanged, toggleIsPreviewLogoChanged] = useState(false);
  const [isDeleteLoading, toggleDeleteLoading] = useState(false);
  const dispatch = useDispatch();
  const { isEditing, benefitLevel } = useSelector(
    (state) => state.editBenefitLevel
  );
  const { classes } = useStyles();
  const form = useForm({
    initialValues: {
      benefit: {
        id: benefit.id,
      },
      level: {
        id: "",
      },
      points: 0,
      discount: 0.0,
      days: "",
      maxPerUser: 0,
      image: null,
      previewImage: null,
    },
  });

  // Listen to edit
  useEffect(() => {
    // Check changes
    if (isEditing) {
      // Assign form values
      form.setValues({
        benefit: {
          id: benefit.id,
        },
        level: {
          id: benefitLevel.level.id,
        },
        points: benefitLevel.points,
        discount: benefitLevel.discount,
        days: benefitLevel.days,
        maxPerUser: benefitLevel.maxPerUser,
        image: benefitLevel.image,
        previewImage: benefitLevel.previewImage,
      });
    } else {
      // Reset form
      form.setValues({
        benefit: {
          id: benefit.id,
        },
        level: {
          id: "",
        },
        points: 0,
        discount: 0.0,
        days: "",
        maxPerUser: 0,
        image: null,
        previewImage: null,
      });
    }
    // eslint-disable-next-line
  }, [isEditing, benefitLevel]);

  // On load
  useEffect(() => {
    // Get levels
    getLevels(
      function (response) {
        // Loop levels
        var levels = [];
        for (var i = 0; i < response.data.length; i++) {
          // Assign
          levels.push({
            label: response.data[i].name,
            value: response.data[i].id,
          });
        }

        // Set state
        setLevels(levels);
      },
      function (error) {
        // Show error
        notifications.show({
          title: "Error",
          message: error,
          color: "red",
        });
      }
    );
  }, []);

  // Submit form
  const onSubmit = (values) => {
    // Set loader
    toggleLoading(true);

    if (!values.image || !values.previewImage) {
      notifications.show({
        title: "Error",
        message: "Ambas imágenes (detalle y listado) son requeridas",
        color: "red",
      });
      toggleLoading(false);
      return;
    }
    // Conditions
    if (!isEditing) {
      // Call API
      saveBenefitLevel(
        values,
        function () {
          // Show success
          notifications.show({
            title: "Nivel de beneficio creado correctamente",
            color: "green",
            icon: <IconCheck />,
          });

          // Reload after 1 second
          setTimeout(() => {
            // Remove loader
            toggleLoading(false);

            // Reload list
            reload();
          }, 1000);
        },
        function (error) {
          // Remove preloader
          toggleLoading(false);

          // Show error
          notifications.show({
            title: "Error",
            message: error?.message?.[0] || "Error inesperado.",
            color: "red",
          });
        }
      );
    } else {
      // Call API
      updateBenefitLevel(
        benefitLevel.id,
        values,
        function () {
          // Show success
          notifications.show({
            title: "Nivel de beneficio modificado correctamente",
            color: "green",
            icon: <IconCheck />,
          });

          // Close after 1 second
          setTimeout(() => {
            // End process
            cancelEdit();
          }, 1000);
        },
        function (error) {
          // Remove preloader
          toggleLoading(false);

          // Show error
          notifications.show({
            title: "Error",
            message: error?.message?.[0] || "Error inesperado.",
            color: "red",
          });
        }
      );
    }
  };

  // Validate image dimensions
  const validateImageDimensions = (file, expectedWidth, expectedHeight) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.src = url;
  
      img.onload = () => {
        if (img.width === expectedWidth && img.height === expectedHeight) {
          resolve(url); 
        } else {
          URL.revokeObjectURL(url);
          reject(`La imagen debe tener un tamaño de ${expectedWidth}x${expectedHeight} px`);
        }
      };
  
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject('No se pudo leer la imagen.');
      };
    });
  };

  // Process accepted file
  const onDrop = async (files) => {
    const file = files[0];
    try {
      const url = await validateImageDimensions(file, 720, 548);
      form.setFieldValue("image", file);
      setFileURL(url);
      toggleIsLogoChanged(true);
    } catch (errorMessage) {
      notifications.show({
        title: "Error",
        message: errorMessage || String(errorMessage) || "Error desconocido",
        color: "red",
      });
    }
  };
  
  const onDropPreview = async (files) => {
    const file = files[0];
    try {
      const url = await validateImageDimensions(file, 224, 294);
      form.setFieldValue("previewImage", file);
      setPreviewFileURL(url);
      toggleIsPreviewLogoChanged(true);
    } catch (errorMessage) {
      notifications.show({
        title: "Error",
        message: errorMessage || String(errorMessage) || "Error desconocido",
        color: "red",
      });
    }
  };
  

  // Reject file and show error
  const onReject = (file) => {
    notifications.show({
      title: "Error",
      message: "Este tipo de archivo no está soportado.",
      color: "red",
    });
  };

  // Open delete confirmation modal
  const openDeleteModal = () =>
    modals.openConfirmModal({
      title: "Eliminar Nivel de Beneficio",
      centered: true,
      children: (
        <Text size="sm">
          ¿Estás seguro de querer borrar este nivel de beneficio? Esta acción no
          se puede deshacer.
        </Text>
      ),
      labels: { confirm: "Eliminar nivel de beneficio", cancel: "Cancelar" },
      confirmProps: { color: "red", variant: "light" },
      onCancel: () => {}, // Nothing
      onConfirm: triggerDeleteBenefitLevel,
      closeOnClickOutside: false,
      zIndex: 400,
    });

  // Trigger delete benefit level
  const triggerDeleteBenefitLevel = () => {
    // Set loader
    toggleDeleteLoading(true);

    // Call API
    deleteBenefitLevel(
      benefitLevel.id,
      function () {
        // Show success
        notifications.show({
          title: "Nivel de beneficio eliminado correctamente",
          color: "green",
          icon: <IconCheck />,
        });

        // Close after 1 second
        setTimeout(() => {
          // Remove preloader
          toggleDeleteLoading(false);

          // End process
          cancelEdit();
        }, 1000);
      },
      function (error) {
        // Remove preloader
        toggleDeleteLoading(false);

        // Show error
        notifications.show({
          title: "Error",
          message: error,
          color: "red",
        });
      }
    );
  };

  // Cancel editing
  const cancelEdit = (element) => {
    dispatch.editBenefitLevel.toggleEditing(false);
    dispatch.editBenefitLevel.setBenefitLevel({});
    toggleLoading(false);
  };

  return (
    <>
      {/* Delete Loader */}
      {isDeleteLoading ? (
        <Portal>
          <Overlay sx={{ zIndex: 999 }} center blur={15}>
            <Loader size={100} />
          </Overlay>
        </Portal>
      ) : null}
      {/* Form */}
      <form onSubmit={form.onSubmit(onSubmit)}>
        {/* Input Group */}
        <Group grow>
          {/* Level ID */}
          <Select
            searchable
            withinPortal
            required
            label="Nivel"
            placeholder="Busca un nivel"
            value={form.values.level.id}
            onChange={(event) =>
              form.setFieldValue("level.id", event.currentTarget.value)
            }
            {...form.getInputProps("level.id")}
            nothingFound="Sin resultados"
            data={levels}
          />
        </Group>
        {/* Input Group */}
        <Group grow mt="md">
          {/* Points */}
          <NumberInput
            required
            label="Puntos"
            placeholder="Puntos del nivel"
            value={form.values.points}
            onChange={(event) =>
              form.setFieldValue("points", event.currentTarget.value)
            }
            {...form.getInputProps("points")}
            min={0}
            max={100}
            step={1}
          />
          {/* Discount */}
          <NumberInput
            required
            label="Descuento"
            placeholder="Descuento del nivel"
            value={form.values.discount}
            onChange={(event) =>
              form.setFieldValue("discount", event.currentTarget.value)
            }
            {...form.getInputProps("discount")}
            min={0.0}
            max={
              benefit.discountType.name === "Descuento porcentaje"
                ? 100.0
                : 9999999.99
            }
            step={0.1}
            precision={1}
            disabled={benefit.discountType.name === "2x1"}
            icon={
              benefit.discountType.name === "Descuento porcentaje" ? (
                <IconPercentage size="1rem" />
              ) : benefit.discountType.name === "Descuento fijo" ? (
                <IconCurrencyDollar size="1rem" />
              ) : null
            }
          />
        </Group>
        {/* Input Group */}
        <Group grow mt="md">
          {/* Days */}
          <TextInput
            required
            label="Días"
            placeholder="Días de validez"
            value={form.values.days}
            onChange={(event) =>
              form.setFieldValue("days", event.currentTarget.value)
            }
            {...form.getInputProps("days")}
            maxLength={255}
          />
          {/* Max Per User */}
          <NumberInput
            required
            label="Máximo por Usuario"
            placeholder="Máximo de usos por usuario"
            value={form.values.maxPerUser}
            onChange={(event) =>
              form.setFieldValue("maxPerUser", event.currentTarget.value)
            }
            {...form.getInputProps("maxPerUser")}
            min={0}
            max={100}
            step={1}
          />
        </Group>
        <Space h="md" />
        {/* Subtitle */}
        <Group spacing="xs" align="baseline">
          <Title order={5} mb="xs">
            Imagen detalle
          </Title>
          <Text size="xs" color="dimmed">
            720 x 548
          </Text>
        </Group>
        {/* Dropzone */}
        <Group>
          {/* Zone */}
          <BenefitLevelImageDropzone onDrop={onDrop} onReject={onReject} />
          {/* Preview */}
          {form.values.image !== null ? (
            <Box className={classes.imageContainer}>
              <MantineImage
                withPlaceholder
                src={isEditing && !isLogoChanged ? benefitLevel.image : fileURL}
                imageProps={
                  isEditing && !isLogoChanged
                    ? null
                    : { onLoad: () => URL.revokeObjectURL(fileURL) }
                }
                height={148}
                width={148}
                radius="sm"
              />
            </Box>
          ) : null}
        </Group>
        <Space h="md" />
        <Group spacing="xs" align="baseline">
          {/* Subtitle */}
          <Title order={5} mb="xs">
            Imagen listado
          </Title>
          <Text size="xs" color="dimmed">
            224 x 294
          </Text>
        </Group>
        {/* Dropzone */}
        <Group>
          {/* Zone */}
          <BenefitLevelImageDropzone onDrop={onDropPreview} onReject={onReject} />
          {/* Preview */}
          {form.values.previewImage !== null ? (
            <Box className={classes.imageContainer}>
              <MantineImage
                withPlaceholder
                src={isEditing && !isPreviewLogoChanged ? benefitLevel.previewImage : previewFileURL}
                imageProps={
                  isEditing && !isPreviewLogoChanged
                    ? null
                    : { onLoad: () => URL.revokeObjectURL(fileURL) }
                }
                height={148}
                width={148}
                radius="sm"
              />
            </Box>
          ) : null}
        </Group>
        {/* Buttons */}
        <Group position="apart" grow={!isEditing}>
          {/* Edit Buttons */}
          {isEditing ? (
            <>
              {/* Delete */}
              <UnstyledButton
                className="deleteButton"
                onClick={openDeleteModal}
              >
                <IconTrash size={20} />
              </UnstyledButton>
              {/* Cancel */}
              <Button
                mt="xl"
                color="red"
                onClick={cancelEdit}
                disabled={isLoading}
                variant="light"
                sx={{ flexGrow: isEditing ? 2 : 1 }}
              >
                Cancelar
              </Button>
            </>
          ) : null}
          {/* Submit */}
          <Button
            mt="xl"
            type="submit"
            loading={isLoading}
            variant="filled"
            sx={{ flexGrow: 1 }}
          >
            {isEditing ? "Guardar Cambios" : "Crear Nivel de Beneficio"}
          </Button>
        </Group>
      </form>
    </>
  );
};

export default BenefitLevelForm;
